import { useEffect, useState, useCallback } from "react";
import { api, BREEDS, BREED_LABELS, MULTIPLIERS, fmt, errMsg, API, openServerFile } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyRow } from "@/components/shared";
import { F, In, Sel, DelBtn, Td, TableCard } from "@/pages/Feed";
import { toast } from "sonner";
import { Plus, Truck, Users, AlertTriangle, FileText, Receipt, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

export default function Shipment() {
  const { coops, coopName, meta, selectedCoop } = useApp();
  const locations = coops.filter((c) => c.type === "location" || c.type === "warehouse");
  const allItems = [...meta.egg_sizes, ...meta.defects];
  const [tab, setTab] = useState("ship");
  const { can } = useAuth();
  const canFin = can("view_financials");
  const canShip = can("manage_shipping");
  const canCust = can("view_customers");
  let eff = tab;
  if ((eff === "ship" || eff === "planli") && !canShip) eff = "cust";
  if (eff === "cust" && !canCust) eff = canShip ? "ship" : "none";
  const [shipments, setShipments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stock, setStock] = useState([]);
  const [form, setForm] = useState({ coop_id: "", customer_id: "", date: new Date().toISOString().slice(0, 10), plate: "", driver: "", waybill_no: "", payment_due_date: "", palette_count: "", palette_price: "", previous_balance: "" });
  const [activeBreed, setActiveBreed] = useState("kirmizi");
  const [qty, setQty] = useState({});    // { coop: { breed|'_fire': { size: val } } }
  const [price, setPrice] = useState({});
  const [extraLines, setExtraLines] = useState([]);
  const [cForm, setCForm] = useState({ name: "", contact: "", address: "" });
  const [stmtId, setStmtId] = useState(null);

  const onCustomerChange = (cid) => {
    const cust = customers.find((c) => c.id === cid);
    setForm((f) => ({ ...f, customer_id: cid, previous_balance: cust && cust.balance != null ? String(cust.balance) : "0" }));
  };

  const load = useCallback(async () => {
    const q = selectedCoop !== "all" ? `?coop_id=${selectedCoop}` : "";
    const [c, st] = await Promise.all([api.get("/customers"), api.get("/stock")]);
    setCustomers(c.data); setStock(st.data);
    if (canShip) {
      const s = await api.get(`/shipments${q}`);
      setShipments(s.data);
    }
  }, [selectedCoop, canShip]);
  useEffect(() => { load(); }, [load]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const visibleShipments = eff === "planli" ? shipments.filter((s) => s.date > todayStr) : shipments;

  const reschedule = async (id, newDate) => {
    try { await api.patch(`/shipments/${id}`, { date: newDate }); toast.success("Planlı sevkiyat tarihi güncellendi"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const ac = form.coop_id;
  const eggSizes = meta.egg_sizes;
  const defects = meta.defects;
  const gq = (k, size) => qty[ac]?.[k]?.[size] ?? "";
  const gp = (k, size) => price[ac]?.[k]?.[size] ?? "";
  const setQ = (k, size, val) => setQty((p) => ({ ...p, [ac]: { ...(p[ac] || {}), [k]: { ...((p[ac] || {})[k] || {}), [size]: val } } }));
  const setP = (k, size, val) => setPrice((p) => ({ ...p, [ac]: { ...(p[ac] || {}), [k]: { ...((p[ac] || {})[k] || {}), [size]: val } } }));
  const eggStock = (size) => stock.find((s) => s.coop_id === ac && s.breed === activeBreed)?.sizes?.[size] ?? 0;
  const fireStock = (size) => stock.filter((s) => s.coop_id === ac).reduce((a, d) => a + (d.sizes?.[size] || 0), 0);
  const setExtra = (i, key, val) => setExtraLines((es) => es.map((e, idx) => idx === i ? { ...e, [key]: val } : e));
  const addExtra = () => setExtraLines((es) => [...es, { name: "", qty: "", price: "" }]);
  const removeExtra = (i) => setExtraLines((es) => es.filter((_, idx) => idx !== i));

  const coopsWithItems = Object.keys(qty).filter((c) => Object.values(qty[c] || {}).some((m) => Object.values(m || {}).some((v) => Number(v) > 0)));
  const tl = (v) => (Number(v) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " TL";

  const buildItems = () => {
    const items = [];
    Object.entries(qty).forEach(([coop, keys]) => Object.entries(keys).forEach(([k, sizes]) => Object.entries(sizes).forEach(([size, v]) => {
      const c = Number(v || 0);
      if (c <= 0) return;
      const pr = Number(price[coop]?.[k]?.[size] || 0);
      if (k === "_fire") items.push({ coop_id: coop, size, cartons: c, price: pr, is_fire: true });
      else items.push({ coop_id: coop, breed: k, size, cartons: c, price: pr });
    })));
    return items;
  };
  const buildExtra = () => extraLines.filter((e) => (e.name || "").trim() && Number(e.qty)).map((e) => ({ name: e.name.trim(), qty: Number(e.qty), price: Number(e.price || 0) }));

  const allBuilt = buildItems();
  const grandTotal = allBuilt.reduce((s, i) => s + i.cartons, 0);
  const itemsAmount = allBuilt.reduce((s, i) => s + i.price * (MULTIPLIERS[i.size] || 0) * i.cartons, 0);
  const extraAmount = extraLines.reduce((s, e) => s + Number(e.qty || 0) * Number(e.price || 0), 0);
  const paletteAmount = Number(form.palette_count || 0) * Number(form.palette_price || 0);
  const goodsTotal = itemsAmount + paletteAmount + extraAmount;
  const grandAmount = goodsTotal + Number(form.previous_balance || 0);

  const submit = async (force = false) => {
    if (!form.customer_id) return toast.error("Müşteri seçin");
    const items = buildItems();
    if (items.length === 0) return toast.error("En az bir kalem girin");
    try {
      const payload = {
        ...form,
        previous_balance: Number(form.previous_balance || 0),
        palette_count: Number(form.palette_count || 0),
        palette_price: Number(form.palette_price || 0),
        items,
        extra_items: buildExtra(),
      };
      await api.post(`/shipments?force=${force}`, payload);
      toast.success("Sevkiyat kaydedildi, konsolide stoktan düşüldü");
      setForm({ ...form, plate: "", driver: "", waybill_no: "", payment_due_date: "", palette_count: "", palette_price: "", previous_balance: "" });
      setQty({}); setPrice({}); setExtraLines([]);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.shortages) {
        const list = detail.shortages.map((s) => `${s.coop_name ? s.coop_name + " · " : ""}${s.breed_label} ${s.label}: ist. ${s.requested} / stok ${s.available}`).join("\n");
        if (window.confirm(`Yetersiz stok!\n${list}\n\nYine de eksi stoğa düşerek kaydedilsin mi?`)) submit(true);
        else toast.error("Sevkiyat iptal edildi");
      } else toast.error(errMsg(e));
    }
  };

  const saveCustomer = async () => {
    if (!cForm.name) return toast.error("Müşteri adı zorunlu");
    await api.post("/customers", cForm);
    toast.success("Müşteri eklendi");
    setCForm({ name: "", contact: "", address: "" });
    load();
  };

  const shipBreeds = (s) => [...new Set((s.items || []).map((i) => i.breed))].map((b) => BREED_LABELS[b] || b).join(", ");
  const shipSources = (s) => [...new Set((s.items || []).map((i) => i.coop_id || s.coop_id))].map((id) => coopName(id)).join(", ") || coopName(s.coop_id);

  return (
    <div>
      <PageHeader title="Sevkiyat & Stok Düşüşü" subtitle="Tek sevkiyatta çoklu ırk + fire kalemleri · konsolide (lokasyon+çeşit) stoktan otomatik düşülür" />
      <div className="flex gap-2 mb-4">
        {canShip && <TabBtn active={eff === "ship"} onClick={() => setTab("ship")} testid="ship-tab-ship"><Truck size={15} className="inline mr-1.5" />Sevkiyat</TabBtn>}
        {canShip && <TabBtn active={eff === "planli"} onClick={() => setTab("planli")} testid="ship-tab-planli"><AlertTriangle size={15} className="inline mr-1.5" />Planlı Sevkiyat</TabBtn>}
        {canCust && <TabBtn active={eff === "cust"} onClick={() => setTab("cust")} testid="ship-tab-cust"><Users size={15} className="inline mr-1.5" />Müşteriler</TabBtn>}
      </div>

      {eff === "none" ? (
        <div className="text-muted-foreground py-10 text-center">Bu bölüm için yetkiniz yok.</div>
      ) : eff !== "cust" ? (
        <>
          {eff === "planli" && (
            <div className="mb-4 rounded-lg bg-primary/5 border border-primary/20 text-sm px-4 py-3" data-testid="planli-banner">
              İleri tarihli (planlı) sevkiyatlar burada listelenir ve <strong>Stok</strong> projeksiyonundan o günlerde otomatik düşülür.
            </div>
          )}
          <div className="rounded-xl border border-border bg-card p-5 mb-6" data-testid="ship-form">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
              <F label="Çıkış Kümes / Depo"><Sel value={form.coop_id} onChange={(v) => setForm({ ...form, coop_id: v })} opts={locations} testid="sh-coop" /></F>
              <F label="Müşteri"><select value={form.customer_id} onChange={(e) => onCustomerChange(e.target.value)} data-testid="sh-cust" className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none"><option value="">Seçin</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></F>
              <F label="Tarih"><In type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="sh-date" /></F>
              <F label="Plaka"><In value={form.plate} onChange={(v) => setForm({ ...form, plate: v })} testid="sh-plate" /></F>
              <F label="İrsaliye No"><In value={form.waybill_no} onChange={(v) => setForm({ ...form, waybill_no: v })} testid="sh-waybill" /></F>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
              <F label="Teslim Alan / Şoför"><In value={form.driver} onChange={(v) => setForm({ ...form, driver: v })} testid="sh-driver" /></F>
              <F label="Ödeme / Vade (Çek) Tarihi"><In type="date" value={form.payment_due_date} onChange={(v) => setForm({ ...form, payment_due_date: v })} testid="sh-due" /></F>
              <F label="Palet Adedi"><In type="number" value={form.palette_count} onChange={(v) => setForm({ ...form, palette_count: v })} testid="sh-palette-count" /></F>
              {canFin && <F label="Palet Birim Fiyatı (₺)"><In type="number" value={form.palette_price} onChange={(v) => setForm({ ...form, palette_price: v })} testid="sh-palette-price" /></F>}
              {canFin && <F label="Eski Bakiye (₺)"><In type="number" value={form.previous_balance} onChange={(v) => setForm({ ...form, previous_balance: v })} testid="sh-prev-balance" /></F>}
            </div>

            {/* Çıkış kümesi seçimi + çoklu kümes özeti */}
            {coopsWithItems.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-3 text-xs" data-testid="sh-coops-summary">
                <span className="text-muted-foreground">Fişteki kümesler:</span>
                {coopsWithItems.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, coop_id: c })} className={`px-2.5 py-1 rounded-full font-semibold transition-colors ${ac === c ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-secondary/70"}`}>{coopName(c)}</button>
                ))}
              </div>
            )}
            {!ac ? (
              <div className="rounded-lg border border-dashed border-border bg-secondary/20 py-6 text-center text-sm text-muted-foreground mb-4">Kalem eklemek için yukarıdan bir <strong>Çıkış Kümesi</strong> seçin. Farklı kümeslerden mal eklemek için kümesi değiştirip devam edin (tek fişe eklenir).</div>
            ) : (
              <>
                {/* breed tabs */}
                <div className="flex gap-2 mb-3">
                  {BREEDS.map((b) => (
                    <button key={b} onClick={() => setActiveBreed(b)} data-testid={`sh-breed-tab-${b}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${activeBreed === b ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      {BREED_LABELS[b]}
                    </button>
                  ))}
                </div>
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">{coopName(ac)} · {BREED_LABELS[activeBreed]} — Standart Yumurta (viyol + ₺/adet)</div>
                <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-11 border border-border rounded-lg overflow-hidden mb-4">
                  {eggSizes.map((s) => (
                    <div key={s.key} className="border-r border-b border-border last:border-r-0 flex flex-col">
                      <div className="px-2 py-1 text-[11px] font-semibold text-center bg-secondary/60 border-b border-border">{s.label}
                        <span className="block text-[10px] font-normal text-muted-foreground">stok: {fmt(eggStock(s.key))} · x{MULTIPLIERS[s.key]}</span>
                      </div>
                      <input type="number" min="0" placeholder="viyol" className="grid-input text-center h-8 px-1 text-sm bg-background outline-none" value={gq(activeBreed, s.key)} onChange={(e) => setQ(activeBreed, s.key, e.target.value)} data-testid={`sh-size-${activeBreed}-${s.key}`} />
                      {canFin && <input type="number" min="0" step="0.01" placeholder="₺/adet" className="grid-input text-center h-8 px-1 !text-[11px] text-muted-foreground bg-background border-t border-border/60 outline-none" value={gp(activeBreed, s.key)} onChange={(e) => setP(activeBreed, s.key, e.target.value)} data-testid={`sh-price-${activeBreed}-${s.key}`} />}
                    </div>
                  ))}
                </div>
                {/* Fire yumurta — ırk bağımsız, 4 kalite tipi */}
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">{coopName(ac)} · Fire Yumurta (ırk ayrımı yok — kalite/hasar tipine göre)</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 border border-amber-500/30 rounded-lg overflow-hidden mb-4">
                  {defects.map((s) => (
                    <div key={s.key} className="border-r border-b border-border last:border-r-0 flex flex-col">
                      <div className="px-2 py-1 text-[11px] font-semibold text-center bg-amber-500/10 border-b border-border">{s.label}
                        <span className="block text-[10px] font-normal text-muted-foreground">stok: {fmt(fireStock(s.key))} · x{MULTIPLIERS[s.key]}</span>
                      </div>
                      <input type="number" min="0" placeholder="viyol" className="grid-input text-center h-8 px-1 text-sm bg-background outline-none" value={gq("_fire", s.key)} onChange={(e) => setQ("_fire", s.key, e.target.value)} data-testid={`sh-fire-${s.key}`} />
                      {canFin && <input type="number" min="0" step="0.01" placeholder="₺/adet" className="grid-input text-center h-8 px-1 !text-[11px] text-muted-foreground bg-background border-t border-border/60 outline-none" value={gp("_fire", s.key)} onChange={(e) => setP("_fire", s.key, e.target.value)} data-testid={`sh-fire-price-${s.key}`} />}
                    </div>
                  ))}
                </div>
              </>
            )}
            {canFin && (
              <>
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">Serbest Mal Kalemleri (viyol, koli, gübre vb. — stoğu etkilemez)</div>
                <div className="space-y-2 mb-2" data-testid="sh-extra-lines">
                  {extraLines.map((e, i) => (
                    <div key={i} className="grid grid-cols-2 lg:grid-cols-12 gap-2 items-center" data-testid={`sh-extra-${i}`}>
                      <input placeholder="Ürün / Kalem adı" value={e.name} onChange={(ev) => setExtra(i, "name", ev.target.value)} data-testid={`sh-extra-name-${i}`} className="lg:col-span-5 h-9 rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none" />
                      <input type="number" placeholder="Adet" value={e.qty} onChange={(ev) => setExtra(i, "qty", ev.target.value)} data-testid={`sh-extra-qty-${i}`} className="lg:col-span-2 h-9 rounded-md border border-input bg-background px-2 text-sm text-center focus:ring-2 focus:ring-primary outline-none" />
                      <input type="number" step="0.01" placeholder="Birim ₺" value={e.price} onChange={(ev) => setExtra(i, "price", ev.target.value)} data-testid={`sh-extra-price-${i}`} className="lg:col-span-2 h-9 rounded-md border border-input bg-background px-2 text-sm text-center focus:ring-2 focus:ring-primary outline-none" />
                      <div className="lg:col-span-2 text-sm tabular-nums text-right pr-1">{tl(Number(e.qty || 0) * Number(e.price || 0))}</div>
                      <button onClick={() => removeExtra(i)} data-testid={`sh-extra-del-${i}`} className="lg:col-span-1 justify-self-end text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
                <button onClick={addExtra} data-testid="sh-add-extra" className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-4 py-2 text-xs font-semibold hover:bg-secondary transition-colors mb-4"><Plus size={14} /> Serbest Kalem Ekle</button>
              </>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-3 border-t border-border">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <span className="text-muted-foreground">Toplam Viyol: <strong className="text-foreground tabular-nums" data-testid="sh-grand-total">{fmt(grandTotal)}</strong></span>
                {canFin && <span className="text-muted-foreground">Mal/Hizmet: <strong className="text-foreground tabular-nums">{tl(goodsTotal)}</strong></span>}
                {canFin && Number(form.previous_balance || 0) > 0 && <span className="text-muted-foreground">Eski Bakiye: <strong className="text-foreground tabular-nums">{tl(form.previous_balance)}</strong></span>}
                {canFin && <span className="text-primary font-semibold">Genel Toplam: <strong className="tabular-nums" data-testid="sh-amount-total">{tl(grandAmount)}</strong></span>}
              </div>
              <button onClick={() => submit(false)} data-testid="sh-save" className="sm:ml-auto inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"><Truck size={16} /> Sevkiyatı Kaydet</button>
            </div>
          </div>

          {eff === "planli" ? <PlanCalendar shipments={visibleShipments} coopName={coopName} onDrop={reschedule} /> : (
          <TableCard headers={["Tarih", "Çıkış", "Çeşitler", "Müşteri", "Toplam Krt.", "Plaka", "İrsaliye", ""]}>
            {visibleShipments.length === 0 ? <EmptyRow colSpan={8} text={eff === "planli" ? "Planlı (ileri tarihli) sevkiyat yok." : "Kayıt bulunamadı."} /> : visibleShipments.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-secondary/30" data-testid={`sh-row-${s.id}`}>
                <Td>{s.date}</Td><Td>{shipSources(s)}</Td><Td>{shipBreeds(s)}</Td><Td className="font-medium">{s.customer_name}</Td>
                <Td className="text-right tabular-nums font-semibold">{fmt(s.total)}</Td><Td>{s.plate}</Td><Td>{s.waybill_no}</Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openServerFile(`${API}/shipments/${s.id}/waybill?token=${localStorage.getItem("token")}`, `İrsaliye PDF`)} data-testid={`sh-waybill-${s.id}`} className="text-muted-foreground hover:text-primary transition-colors" title="İrsaliye PDF"><FileText size={16} /></button>
                    {canFin && <button onClick={() => openServerFile(`${API}/shipments/${s.id}/receipt?token=${localStorage.getItem("token")}`, `Satış Fişi PDF`)} data-testid={`sh-receipt-${s.id}`} className="text-muted-foreground hover:text-primary transition-colors" title="Satış Fişi PDF"><Receipt size={16} /></button>}
                    <DelBtn onClick={async () => { await api.delete(`/shipments/${s.id}`); load(); }} />
                  </div>
                </Td>
              </tr>
            ))}
          </TableCard>
          )}
        </>
      ) : stmtId ? (
        <CustomerStatement id={stmtId} onBack={() => { setStmtId(null); load(); }} />
      ) : (
        <>
          <div className="rounded-xl border border-border bg-card p-5 mb-6 grid grid-cols-2 md:grid-cols-4 gap-3 items-end" data-testid="cust-form">
            <F label="Müşteri / Cari Adı"><In value={cForm.name} onChange={(v) => setCForm({ ...cForm, name: v })} testid="cu-name" /></F>
            <F label="İletişim"><In value={cForm.contact} onChange={(v) => setCForm({ ...cForm, contact: v })} testid="cu-contact" /></F>
            <F label="Adres / Sevk Noktası"><In value={cForm.address} onChange={(v) => setCForm({ ...cForm, address: v })} testid="cu-address" /></F>
            <button onClick={saveCustomer} data-testid="cu-save" className="h-9 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={16} /> Ekle</button>
          </div>
          <TableCard headers={["Müşteri", "İletişim", "Adres", ...(canFin ? ["Borç"] : []), ""]}>
            {customers.length === 0 ? <EmptyRow colSpan={canFin ? 5 : 4} /> : customers.map((c) => (
              <tr key={c.id} className="border-t border-border hover:bg-secondary/30" data-testid={`cu-row-${c.id}`}>
                <Td>{canFin ? <button onClick={() => setStmtId(c.id)} data-testid={`cu-open-${c.id}`} className="font-medium text-primary hover:underline text-left">{c.name}</button> : <span className="font-medium">{c.name}</span>}</Td>
                <Td>{c.contact}</Td><Td>{c.address}</Td>
                {canFin && <Td className="text-right" data-testid={`cu-balance-${c.id}`}>
                  <span className={`tabular-nums font-semibold ${(c.balance || 0) > 0 ? "text-destructive" : "text-green-600 dark:text-green-400"}`}>{tlFmt(c.balance || 0)}</span>
                  {c.overdue && <span className="ml-2 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-destructive/15 text-destructive whitespace-nowrap" data-testid={`cu-overdue-${c.id}`}>vadesi geçti</span>}
                </Td>}
                <Td><DelBtn onClick={async () => { await api.delete(`/customers/${c.id}`); load(); }} /></Td>
              </tr>
            ))}
          </TableCard>
        </>
      )}
    </div>
  );
}

const tlFmt = (v) => (Number(v) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " TL";

const StatBox = ({ label, value, tone }) => (
  <div className="rounded-xl border border-border bg-card px-4 py-2.5 text-right min-w-[120px]">
    <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">{label}</div>
    <div className={`font-head font-bold tabular-nums ${tone === "red" ? "text-destructive" : tone === "green" ? "text-green-600 dark:text-green-400" : ""}`}>{value}</div>
  </div>
);

function CustomerStatement({ id, onBack }) {
  const [data, setData] = useState(null);
  const [pf, setPf] = useState({ amount: "", date: new Date().toISOString().slice(0, 10), method: "nakit", note: "" });
  const load = useCallback(async () => {
    const { data } = await api.get(`/customers/${id}/statement`);
    setData(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const addPayment = async () => {
    if (!Number(pf.amount)) return toast.error("Tutar girin");
    await api.post("/payments", { customer_id: id, amount: Number(pf.amount), date: pf.date, method: pf.method, note: pf.note });
    toast.success("Ödeme (tahsilat) kaydedildi");
    setPf({ ...pf, amount: "", note: "" });
    load();
  };

  if (!data) return <div className="text-muted-foreground py-10 text-center">Yükleniyor…</div>;
  const { customer, shipments, payments, total_debit, total_paid, balance } = data;

  return (
    <div data-testid="customer-statement">
      <button onClick={onBack} data-testid="stmt-back" className="text-sm text-primary mb-4 inline-flex items-center gap-1 hover:underline"><ChevronLeft size={16} /> Müşterilere dön</button>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="font-head text-2xl font-bold" data-testid="stmt-name">{customer.name}</h2>
            <button onClick={() => openServerFile(`${API}/customers/${id}/statement/pdf?token=${localStorage.getItem("token")}`, `Müşteri Ekstre PDF`)} data-testid="stmt-pdf" className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-3 h-8 text-xs font-semibold hover:bg-secondary transition-colors"><FileText size={14} /> Ekstre PDF</button>
          </div>
          <p className="text-sm text-muted-foreground">{[customer.contact, customer.address].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="flex gap-3">
          <StatBox label="Toplam Sevkiyat" value={tlFmt(total_debit)} />
          <StatBox label="Toplam Tahsilat" value={tlFmt(total_paid)} tone="green" />
          <StatBox label="Kalan Borç" value={tlFmt(balance)} tone={balance > 0 ? "red" : "green"} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-semibold mb-2">Sevkiyatlar (İrsaliye + Tutar)</h3>
          <TableCard headers={["Tarih", "Fiş No", "Tutar", ""]}>
            {shipments.length === 0 ? <EmptyRow colSpan={4} text="Sevkiyat yok." /> : shipments.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-secondary/30" data-testid={`stmt-ship-${s.id}`}>
                <Td>{s.date}</Td><Td>{s.waybill_no || s.id.slice(0, 8)}</Td>
                <Td className="text-right tabular-nums font-medium">{tlFmt(s.amount)}</Td>
                <Td><button onClick={() => openServerFile(`${API}/shipments/${s.id}/receipt?token=${localStorage.getItem("token")}`, `Satış Fişi PDF`)} className="text-muted-foreground hover:text-primary transition-colors" title="Satış Fişi"><Receipt size={15} /></button></Td>
              </tr>
            ))}
          </TableCard>
        </div>
        <div>
          <h3 className="font-semibold mb-2">Ödemeler (Tahsilat)</h3>
          <div className="rounded-xl border border-border bg-card p-4 mb-3 grid grid-cols-2 gap-3 items-end" data-testid="payment-form">
            <F label="Tutar (₺)"><In type="number" value={pf.amount} onChange={(v) => setPf({ ...pf, amount: v })} testid="pay-amount" /></F>
            <F label="Tarih"><In type="date" value={pf.date} onChange={(v) => setPf({ ...pf, date: v })} testid="pay-date" /></F>
            <F label="Yöntem"><select value={pf.method} onChange={(e) => setPf({ ...pf, method: e.target.value })} data-testid="pay-method" className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none">{["nakit", "çek", "havale", "kredi kartı"].map((m) => <option key={m} value={m}>{m}</option>)}</select></F>
            <button onClick={addPayment} data-testid="pay-save" className="h-9 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={15} /> Ödeme Ekle</button>
          </div>
          <TableCard headers={["Tarih", "Yöntem", "Tutar", ""]}>
            {payments.length === 0 ? <EmptyRow colSpan={4} text="Tahsilat kaydı yok." /> : payments.map((p) => (
              <tr key={p.id} className="border-t border-border hover:bg-secondary/30" data-testid={`stmt-pay-${p.id}`}>
                <Td>{p.date}</Td><Td className="capitalize">{p.method}</Td>
                <Td className="text-right tabular-nums font-medium text-green-600 dark:text-green-400">{tlFmt(p.amount)}</Td>
                <Td><DelBtn onClick={async () => { await api.delete(`/payments/${p.id}`); load(); }} /></Td>
              </tr>
            ))}
          </TableCard>
        </div>
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{children}</button>
);

const CAL_COLORS = [
  "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  "bg-teal-500/15 text-teal-700 dark:text-teal-300",
  "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300",
  "bg-orange-500/15 text-orange-700 dark:text-orange-300",
];
const colorFor = (name) => {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) % 997;
  return CAL_COLORS[h % CAL_COLORS.length];
};

function PlanCalendar({ shipments, coopName, onDrop }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const todayStr = new Date().toISOString().slice(0, 10);
  const y = cursor.getFullYear(), m = cursor.getMonth();
  const startWeekday = (new Date(y, m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const byDate = {};
  shipments.forEach((s) => { (byDate[s.date] = byDate[s.date] || []).push(s); });
  const cell = (d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const WD = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
  const legend = [...new Set(shipments.map((s) => s.customer_name).filter(Boolean))];

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="plan-calendar">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCursor(new Date(y, m - 1, 1))} data-testid="cal-prev" className="h-8 w-8 rounded-lg hover:bg-secondary flex items-center justify-center"><ChevronLeft size={18} /></button>
        <div className="font-head font-semibold capitalize">{cursor.toLocaleDateString("tr-TR", { month: "long", year: "numeric" })}</div>
        <button onClick={() => setCursor(new Date(y, m + 1, 1))} data-testid="cal-next" className="h-8 w-8 rounded-lg hover:bg-secondary flex items-center justify-center"><ChevronRight size={18} /></button>
      </div>
      {legend.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="cal-legend">
          {legend.map((n) => <span key={n} className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${colorFor(n)}`}>{n}</span>)}
        </div>
      )}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WD.map((w) => <div key={w} className="text-center text-[11px] font-semibold uppercase text-muted-foreground py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="min-h-[6rem] rounded-lg bg-secondary/20" />;
          const ds = cell(d);
          const isPast = ds <= todayStr;
          const items = byDate[ds] || [];
          return (
            <div key={i} data-testid={`cal-day-${ds}`}
              onDragOver={(e) => { if (!isPast) e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); if (id && !isPast) onDrop(id, ds); }}
              className={`min-h-[6rem] rounded-lg border p-1.5 ${isPast ? "bg-secondary/20 border-border" : "border-border hover:border-primary/40"}`}>
              <div className={`text-xs font-semibold mb-1 ${isPast ? "text-muted-foreground" : ""}`}>{d}</div>
              <div className="space-y-1">
                {items.map((s) => (
                  <div key={s.id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", s.id)}
                    data-testid={`cal-ship-${s.id}`}
                    className={`cursor-grab active:cursor-grabbing rounded-md text-[11px] px-1.5 py-1 leading-tight truncate ${colorFor(s.customer_name)}`}
                    title={`${coopName(s.coop_id)} → ${s.customer_name} (${s.total} karton)`}>
                    {s.customer_name} · {s.total}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-3">Sevkiyat kartını başka bir güne sürükleyip bırakarak planlı tarihini değiştirebilirsiniz. Her müşteri farklı renkle gösterilir.</p>
    </div>
  );
}
